<!--
 * A Vue component for a dual listbox where items can be moved from left to right and vice versa.
 *
 * Properties
 * ==========
 * - titleAvailable -
 * (Optional) The text to show on the label above the left listbox.
 *
 * - titleSelected -
 * (Optional) The text to show on the label above the right listbox.
 *
 * - items -
 * (Optional) An array with all items.
 *
 * - itemsAvailable -
 * (Optional) An array with all available items.
 *
 * - itemsSelected -
 * (Optional) An array with all selected items.
 *
 * - itemDisplayName -
 * (Optional) A string to indicate which property to use for the displaying name.
-->
<template>
    <div>
        <div class="row row-height">
            <div class="col-sm-5">
                <label for="left">{{titleAvailable}}</label>
                <b-form-select id="left"
                               v-model="available"
                               multiple
                               :options="$_sortedOptions()">
                </b-form-select>
            </div>
            <!-- Move left/right buttons -->
            <div class="col-md-2">
                <label>&nbsp;</label>
                <b-button id="moverightAll" block @click="$_selectAll"><em class="fa fa-forward"></em></b-button>
                <b-button id="moveright" block @click="$_selectItems(available,true)"><em class="fa fa-chevron-right"></em></b-button>
                <b-button id="moveleft" block @click="$_deselectItems(selected,true)"><em class="fa fa-chevron-left"></em></b-button>
                <b-button id="moveleftAll" block @click="$_deselectAll"><em class="fa fa-backward"></em></b-button>
            </div>
            <div class="col-sm-5">
                <label for="right">{{titleSelected}}</label>
                <b-form-select id="right"
                               v-model="selected"
                               multiple
                               :options="$_convertToValueTextPair(itemsSelected, 'id', itemDisplayName)">
                </b-form-select>

            </div>
        </div>
        <div v-if="showUpDownButtons" class="row">
            <!-- Move up/down buttons -->
            <div class="col-sm-5"></div>
            <div class="col-md-2"></div>
            <div class="col-sm-5">
                <div>
                    <b-button id="multiselect_move_up" class="floated" @click="$_moveUp(selected[0])"><em class="fa fa-arrow-up"></em></b-button>
                    <b-button id="multiselect_move_down" class="floated" @click="$_moveDown(selected[0])"><em class="fa fa-arrow-down"></em></b-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { orderBy } from 'lodash';
    import { convertToValueTextPair } from '../services/utilities';

    export default {
        name: 'dualListbox',

        props: {
            items: { type: Array, required: false },
            itemsAvailable: { type: Array, required: false },
            itemsSelected: { type: Array, required: false },
            titleAvailable: { type: String, required: false, default: '' },
            titleSelected: { type: String, required: false, default: '' },
            itemDisplayName: { type: String, required: false, default: 'name' },
            showUpDownButtons: { type: Boolean, required: false, default: true },
        },

        data() {
            return {
                itemsLoaded: false,

                available: [],
                selected: [],

                availableItems: [],
                selectedItems: [],
                localItems: null
            };
        },

        methods: {
            $_convertToValueTextPair(inputArray, key, value) {
                return convertToValueTextPair(inputArray, key, value);
            },

            $_updateLists: function () {
                Object.keys(this.localItems).forEach(key => {
                    const item = this.localItems[key];

                    if (item.userEnabled) {
                        this.selectedItems.push(item);
                    } else {
                        this.availableItems.push(item);
                    }
                });
                this.itemsLoaded = true;
            },

            $_moveUp(itemId) {
                if (itemId) {
                    const index1 = this.localItems.findIndex(bl => bl.id === itemId);
                    for (let i = index1 - 1; i >= 0; i--) {
                        // Swap with upper, userEnabled item
                        if (this.localItems[i].userEnabled) {
                            const index2 = this.localItems.findIndex(bl => bl.id === this.localItems[i].id);
                            this.localItems[index1] = this.localItems.splice(index2, 1, this.localItems[index1])[0];
                            break;
                        }
                    }
                }
            },

            $_moveDown(itemId) {
                if (itemId) {
                    const index1 = this.localItems.findIndex(bl => bl.id === itemId);
                    for (let i = index1 + 1; i < this.localItems.length; i++) {
                        // Swap with underlying, userEnabled item
                        if (this.localItems[i].userEnabled) {
                            const index2 = this.localItems.findIndex(bl => bl.id === this.localItems[i].id);
                            this.localItems[index1] = this.localItems.splice(index2, 1, this.localItems[index1])[0];
                            break;
                        }
                    }
                }
            },

            $_selectItems(itemIds, doUpdate) {
                if (itemIds.length > 0) {
                    itemIds.forEach(itemId => {
                        // Find the index of selected item
                        const selectedItemIndex = this.localItems.findIndex(function (element) {
                            return element.id === itemId;
                        });
                        // Set the userEnabled property to true
                        if (selectedItemIndex > -1) {
                            this.localItems[selectedItemIndex].userEnabled = true;
                        }
                    });
                    // Update the lists
                    if (doUpdate) {
                        this.$_updateLists();
                    }
                }
            },

            $_deselectItems(itemIds, doUpdate) {
                if (itemIds.length > 0) {
                    itemIds.forEach(itemId => {
                        // Find the index of selected item
                        const selectedItemIndex = this.localItems.findIndex(function (element) {
                            return element.id === itemId;
                        });
                        // Set the userEnabled property to false
                        if (selectedItemIndex > -1) {
                            this.localItems[selectedItemIndex].userEnabled = false;
                        }
                    });
                    // Update the lists
                    if (doUpdate) {
                        this.$_updateLists();
                    }
                }
            },

            $_selectAll() {
                // Check wether the items are loaded
                if (!this.itemsLoaded) {
                    this.$_updateLists();
                }
                // Select all deselected items
                Object.keys(this.availableItems).forEach(key => {
                    const item = this.availableItems[key];
                    this.$_selectItems([item.id], false);
                });
                // Update the lists
                this.$_updateLists();
            },

            $_deselectAll() {
                // Check wether the items are loaded
                if (!this.itemsLoaded) {
                    this.$_updateLists();
                }
                // Deselect all selected items
                Object.keys(this.selectedItems).forEach(key => {
                    const item = this.selectedItems[key];
                    this.$_deselectItems([item.id], false);
                });
                // Update the lists
                this.$_updateLists();
            },

            $_sortedOptions() {
                const orderedOptions = orderBy(this.itemsAvailable, [item => item[`${this.itemDisplayName}`].toLowerCase()]);
                return convertToValueTextPair(orderedOptions, 'id', this.itemDisplayName);
            }
        },
        watch: {
            items: {
                handler: function (newVal) {
                    if (newVal === undefined) {
                        return;
                    }
                    this.localItems = JSON.parse(JSON.stringify(newVal));
                },
                deep: true,
                immediate: true
            },
            localItems: {
                handler: function (newVal) {
                    if (JSON.stringify(newVal) === JSON.stringify(this.items)) {
                        return;
                    }
                    const locItems = JSON.parse(JSON.stringify(newVal));
                    this.$emit('update', locItems);
                },
                deep: true
            }
        }

    };
</script>

<style scoped>
    .floated {
        float: right;
        margin-right: 22%;
        width: 18%;
    }

    #left, #right {
        height: 175px;
    }

    .row-height {
        height: 210px;
    }
</style>
